import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, IconButton, Modal, Skeleton, Typography } from '@mui/material';
import { useSnapCarousel } from 'react-snap-carousel';
import { DismissIcon } from '@wavetrak/theme';
import CloudflareResizedImage from 'components/CloudflareResizedImage';
import useCameraMatchConditions from 'hooks/useCameraMatchConditions';
import type { CamForecastComparison, CamForecastTopMatch } from 'types/camera';

import { CamMatchesForecastComparisonCards } from './CamMatchesForecastComparisonCards';
import { camMatchTimestampSeconds } from './formatCamMatchConditions';
import { formatCamMatchDateTime, formatForecastComparisonLabel } from './formatCamMatchDisplay';
import {
  CAM_MATCH_MODAL_IMAGE_HEIGHT,
  CAM_MATCH_MODAL_IMAGE_QUALITY,
  CAM_MATCH_MODAL_IMAGE_WIDTH,
  CAM_MATCH_MODAL_TABLET_LG,
} from './prefetchCamMatchThumbnails';
import styles from './CamMatchesForecastModal.module.scss';

const MODAL_IMAGE_SIZES = `(max-width: ${CAM_MATCH_MODAL_TABLET_LG}px) 100vw, ${CAM_MATCH_MODAL_IMAGE_WIDTH}px`;
const MODAL_IMAGE_CLOUDFLARE_OPTIONS = { fit: 'cover' as const };

export interface CamMatchesForecastModalProps {
  open: boolean;
  onClose: () => void;
  spotId: string;
  match: CamForecastTopMatch;
  forecastComparison: Pick<CamForecastComparison, 'datetime' | 'utcOffset'> | null;
}

export const CamMatchesForecastModal: React.FC<CamMatchesForecastModalProps> = ({
  open,
  onClose,
  spotId,
  match,
  forecastComparison,
}) => {
  const images = match.multipleThumbnailUrls.filter(Boolean);
  const imageCount = images.length;
  const [isCarouselImageLoading, setIsCarouselImageLoading] = useState(true);
  const activePageIndexRef = useRef(0);
  const loadedSlideIndexesRef = useRef<Set<number>>(new Set());

  const snapCarouselOptions = useMemo(
    () =>
      imageCount > 1
        ? { initialPages: Array.from({ length: imageCount }, (_, index) => [index]) }
        : undefined,
    [imageCount],
  );

  const {
    activePageIndex,
    goTo,
    hasNextPage,
    hasPrevPage,
    next,
    pages,
    prev,
    refresh,
    scrollRef,
    snapPointIndexes,
  } = useSnapCarousel(snapCarouselOptions);

  activePageIndexRef.current = activePageIndex;

  const camTimestampSeconds = useMemo(
    () => camMatchTimestampSeconds(match.localTimestamp),
    [match.localTimestamp],
  );
  const forecastTimestampSeconds = forecastComparison?.datetime ?? null;

  const { cam, forecast, isLoading } = useCameraMatchConditions({
    spotId,
    enabled: open,
    camTimestampSeconds,
    forecastTimestampSeconds,
  });

  useEffect(() => {
    if (!open) return;
    loadedSlideIndexesRef.current.clear();
    goTo(0);
    setIsCarouselImageLoading(true);
  }, [open, match.exemplarId]);

  useEffect(() => {
    setIsCarouselImageLoading(!loadedSlideIndexesRef.current.has(activePageIndex));
  }, [activePageIndex]);

  useEffect(() => {
    refresh();
  }, [images.length, refresh]);

  const handleCarouselImageLoaded = useCallback((imageIndex: number) => {
    loadedSlideIndexesRef.current.add(imageIndex);
    if (imageIndex === activePageIndexRef.current) {
      setIsCarouselImageLoading(false);
    }
  }, []);

  const handleCarouselImageError = useCallback((imageIndex: number) => {
    loadedSlideIndexesRef.current.add(imageIndex);
    if (imageIndex === activePageIndexRef.current) {
      setIsCarouselImageLoading(false);
    }
  }, []);

  const camLabel = formatCamMatchDateTime(match.localTimestamp);
  const forecastLabel =
    forecastComparison != null
      ? formatForecastComparisonLabel(forecastComparison.datetime, forecastComparison.utcOffset)
      : '—';

  const overall = Math.round(match.overallSimilarity);
  const units = cam?.units ?? forecast?.units;

  if (!images.length) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="cam-matches-forecast-modal-title"
      slotProps={{ backdrop: { 'aria-hidden': true } }}
    >
      <Box className={styles.centerWrap}>
        <Box
          className={styles.paper}
          data-testid="cam-matches-forecast-modal"
          role="dialog"
          aria-modal="true"
        >
          <IconButton
            type="button"
            className={styles.dismiss}
            data-testid="cam-matches-forecast-modal-close"
            aria-label="Close forecast match"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={onClose}
          >
            <DismissIcon />
          </IconButton>

          <Typography component="h2" id="cam-matches-forecast-modal-title" className={styles.title}>
            Forecast match
          </Typography>

          <Box className={styles.body}>
            <Box className={styles.left}>
              <Typography component="span" className={styles.srOnly}>
                Image {activePageIndex + 1} of {images.length}
              </Typography>

              <Box className={styles.carousel} aria-busy={isCarouselImageLoading}>
                <ul className={styles.carouselScroll} ref={scrollRef}>
                  {images.map((imageUrl, index) => (
                    <li
                      key={imageUrl}
                      className={styles.carouselSlide}
                      data-should-snap="true"
                      style={{
                        scrollSnapAlign: snapPointIndexes?.has(index) ? 'start' : 'none',
                      }}
                    >
                      <Box className={styles.carouselImageWrap}>
                        <Box
                          className={classNames(
                            styles.carouselImage,
                            index === activePageIndex &&
                              isCarouselImageLoading &&
                              styles.carouselImageHidden,
                          )}
                          data-testid="cam-matches-forecast-modal-carousel-image"
                          data-active={index === activePageIndex ? 'true' : 'false'}
                          data-loading={
                            index === activePageIndex && isCarouselImageLoading ? 'true' : 'false'
                          }
                        >
                          <CloudflareResizedImage
                            key={imageUrl}
                            src={imageUrl}
                            alt={`Cam match still ${index + 1} of ${images.length}`}
                            width={CAM_MATCH_MODAL_IMAGE_WIDTH}
                            height={CAM_MATCH_MODAL_IMAGE_HEIGHT}
                            sizes={MODAL_IMAGE_SIZES}
                            layout="fill"
                            loading="eager"
                            objectFit="cover"
                            objectPosition="center"
                            quality={CAM_MATCH_MODAL_IMAGE_QUALITY}
                            cloudflareOptions={MODAL_IMAGE_CLOUDFLARE_OPTIONS}
                            onLoadingComplete={() => handleCarouselImageLoaded(index)}
                            onError={() => handleCarouselImageError(index)}
                          />
                        </Box>
                      </Box>
                    </li>
                  ))}
                </ul>
                {isCarouselImageLoading ? (
                  <Skeleton
                    className={styles.carouselImageSkeleton}
                    variant="rectangular"
                    animation="wave"
                    width="100%"
                    height="100%"
                    aria-hidden
                    data-testid="cam-matches-forecast-modal-carousel-skeleton"
                  />
                ) : null}
                <Box aria-hidden className={styles.carouselGradient} />
                {images.length > 1 ? (
                  <>
                    <Box className={styles.carouselNav}>
                      <IconButton
                        type="button"
                        className={styles.carouselNavBtn}
                        aria-label="Previous image"
                        disabled={!hasPrevPage}
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        onClick={() => prev()}
                      >
                        <ChevronLeftRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        type="button"
                        className={styles.carouselNavBtn}
                        aria-label="Next image"
                        disabled={!hasNextPage}
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        onClick={() => next()}
                      >
                        <ChevronRightRoundedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box
                      className={styles.carouselPagination}
                      data-testid="cam-matches-forecast-modal-pagination"
                    >
                      {pages.map((_, pageIndex) => (
                        <button
                          key={`dot-${pageIndex}`}
                          type="button"
                          className={classNames(
                            styles.carouselDot,
                            pageIndex === activePageIndex && styles.carouselDotActive,
                          )}
                          aria-label={`Go to image ${pageIndex + 1}`}
                          aria-current={pageIndex === activePageIndex ? 'true' : undefined}
                          onClick={() => goTo(pageIndex)}
                        />
                      ))}
                    </Box>
                  </>
                ) : null}
              </Box>
            </Box>

            <Box className={styles.right}>
              <Box className={styles.headerRow}>
                <Box className={styles.headerCol}>
                  <Typography component="span" className={styles.headerLabel}>
                    Cam match
                  </Typography>
                  <Typography component="span" className={styles.headerValue}>
                    {camLabel || '—'}
                  </Typography>
                </Box>

                <Box className={styles.connector}>
                  <Box className={styles.connectorDot} aria-hidden />
                  <Box className={styles.connectorLine} aria-hidden />
                  <Box className={styles.connectorCenter}>
                    <Typography component="span" className={styles.connectorPct}>
                      {overall}%
                    </Typography>
                    <Typography component="span" className={styles.connectorMatchLabel}>
                      Match
                    </Typography>
                  </Box>
                  <Box className={styles.connectorLine} aria-hidden />
                  <Box className={styles.connectorDot} aria-hidden />
                </Box>

                <Box className={styles.headerColEnd}>
                  <Typography component="span" className={styles.headerLabel}>
                    Forecast
                  </Typography>
                  <Typography component="span" className={styles.headerValue}>
                    {forecastLabel}
                  </Typography>
                </Box>
              </Box>

              <CamMatchesForecastComparisonCards
                camConditions={cam?.data.conditions}
                forecastConditions={forecast?.data.conditions}
                units={units}
                swellSimilarity={match.swellSimilarity}
                windSimilarity={match.windSimilarity}
                tideSimilarity={match.tideSimilarity}
                isLoading={isLoading}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
